class EsignModal extends HTMLElement {
  constructor() {
    super();
    this.id = this.getAttribute('id');
    this.isAlert = this.hasAttribute('data-alert');
    this.showHash = !this.hasAttribute('data-hide-hash');
    this.backgroundScroll = this.hasAttribute('data-background-scroll');
    this.containsVideo = this.hasAttribute('data-contains-video');
    this.lock = this.hasAttribute('data-lock');
    this.showTimeout = this.getAttribute('data-show-timeout') || 0;
    this.hideTimeout = this.getAttribute('data-hide-timeout') || 0;

    // State
    this.modalDialog = null;
    this.modalDocument = null;
    this.closeTriggers = null;
    this.focusableElements = null;
    this.eventListeners = [];
    this.isOpening = false;
    this.isClosing = false;
    this.state = null;

    // Class names
    this.activeClass = 'esign-modal--active';
    this.beforeShowClass = 'esign-modal--before-show';
    this.beforeHideClass = 'esign-modal--before-hide';
  }

  connectedCallback() {
    this.init();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  init() {
    this.addA11Y();
    this.focusableElements = [
      ...this.querySelectorAll(
        'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [contenteditable], [tabindex]:not([tabindex^="-"])'
      ),
    ];
    this.closeTriggers = [...this.querySelectorAll('.js-modal-close')];
  }

  addA11Y() {
    this.setAttribute('aria-hidden', true);

    this.modalVaWrap = document.createElement('div');
    this.modalVaWrap.classList.add('esign-modal__va-wrap');

    this.modalVaM = document.createElement('div');
    this.modalVaM.classList.add('esign-modal__va-m');

    this.modalDialog = document.createElement('div');
    const role = this.isAlert ? 'alertdialog' : 'dialog';
    this.modalDialog.setAttribute('role', role);
    this.modalDialog.setAttribute('aria-modal', 'true');
    this.modalDialog.classList.add('esign-modal__dialog');

    this.modalDocument = document.createElement('div');
    this.modalDocument.setAttribute('role', 'document');

    const originalContent = this.innerHTML;
    this.innerHTML = '';
    this.appendChild(this.modalVaWrap);
    this.modalVaWrap.appendChild(this.modalVaM);
    this.modalVaM.appendChild(this.modalDialog);
    this.modalDialog.appendChild(this.modalDocument);
    this.modalDocument.innerHTML = originalContent;
  }

  show(trigger = null) {
    if (this.isOpening) return;

    console.log('show modal');

    this.isOpening = true;
    this.isClosing = false;
    this.state = 'before-show';

    if (this.containsVideo) {
      this.toggleIframeSrc(true);
    }

    if (!this.backgroundScroll) {
      bodyScrollLock.disableBodyScroll(this, { reserveScrollBarGap: true });
    }

    console.log('beforeShowClass', this.beforeShowClass);
    console.log('beforeHideClass', this.beforeHideClass);

    clearTimeout(this.hideTimeoutReference);
    this.classList.add(this.beforeShowClass);
    this.classList.remove(this.beforeHideClass);
    this.setAttribute('aria-hidden', false);

    this.dispatchEvent(new CustomEvent('before-show'));

    this.showTimeoutReference = setTimeout(() => {
      this.showModal();
    }, this.showTimeout);
  }

  hide() {
    if (this.isClosing) return;

    this.isClosing = true;
    this.isOpening = false;
    this.state = 'before-hide';

    if (!this.backgroundScroll) {
      bodyScrollLock.enableBodyScroll(this);
    }

    clearTimeout(this.showTimeoutReference);
    this.setAttribute('aria-hidden', true);

    // Dispatch custom event before hiding
    this.dispatchEvent(new CustomEvent('before-hide', {
      bubbles: true,
      detail: { modal: this }
    }));

    this.classList.add(this.beforeHideClass);
    this.classList.remove(this.beforeShowClass);

    if (this.containsVideo) {
      this.toggleIframeSrc(false);
    }

    this.hideTimeoutReference = setTimeout(() => {
      this.hideModal();
    }, this.hideTimeout);
  }

  showModal() {
    this.state = 'after-show';
    this.modalDialog.setAttribute('tabindex', -1);
    this.modalDialog.scrollTop = 0;
    this.classList.remove(this.beforeShowClass);
    this.classList.add(this.activeClass);

    this.dispatchEvent(new CustomEvent('show'));

    this.isOpening = false;

    setTimeout(() => {
      this.modalDialog.focus({ preventScroll: true });
      const autofocus = this.modalDialog.querySelector('[autofocus]');
      if (autofocus) {
        autofocus.focus({ preventScroll: true });
      }
    }, 50);
  }

  hideModal() {
    this.state = 'after-hide';
    this.modalDialog.removeAttribute('tabindex');
    this.classList.remove(this.beforeHideClass);
    this.classList.remove(this.activeClass);

    this.dispatchEvent(new CustomEvent('hide'));

    this.isClosing = false;
  }

  setupEventListeners() {
    this.modalVaM.addEventListener('click', (e) => {
      if (e.target === this.modalVaM && !this.lock) {
        this.hide();
      }
    });

    this.closeTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => this.hide());
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.lock) {
        this.hide();
      }

      if (e.key === 'Tab') {
        const focusedIndex = this.focusableElements.indexOf(document.activeElement);
        if (e.shiftKey && (focusedIndex === 0 || focusedIndex === -1)) {
          this.focusableElements[this.focusableElements.length - 1].focus();
          e.preventDefault();
        } else if (!e.shiftKey && focusedIndex === this.focusableElements.length - 1) {
          this.focusableElements[0].focus();
          e.preventDefault();
        }
      }
    });
  }

  toggleIframeSrc(active) {
    const iframe = this.querySelector('iframe[data-src]');
    if (!iframe) return;

    if (active) {
      iframe.setAttribute('src', iframe.dataset.src);
    } else {
      iframe.removeAttribute('src');
    }
  }
}

class ModalManager {
  constructor() {
    this.modalQuery = 'esign-modal';
    this.idModalMap = new Map();
    this.activeModal = null;
    this.activeModalTrigger = null;
    this.isOpeningModal = false;
    this.isClosingModal = false;
    this.showModalTimeoutReference = null;
    this.init();
    console.log('ModalManager');
  }

  init() {
    this.createModals();
    this.bindModalTriggers();
    this.bindWindowPopState();
    this.checkModalOnPageLoad();
  }

  createModals() {
    [...document.querySelectorAll(this.modalQuery)].forEach((modal) => {
      this.idModalMap.set(modal.id, modal);

      modal.addEventListener('before-hide', () => {
        this.onModalBeforeHide(modal);
      });

      modal.addEventListener('hide', () => {
        this.onModalHide(modal);
      });

      modal.addEventListener('before-show', () => {
        this.onModalBeforeShow(modal);
      });

      modal.addEventListener('show', () => {
        this.onModalShow(modal);
      });
    });
  }

  checkModalOnPageLoad() {
    setTimeout(() => {
      const hashOnPageLoad = window.location.hash.substring(1);
      const modal = this.idModalMap.get(hashOnPageLoad);
      if (modal) {
        modal.show();
      }
    });
  }

  onModalBeforeHide() {
    this.isOpeningModal = false;
    this.isClosingModal = true;
    this.activeModalTrigger = false;
    clearTimeout(this.showModalTimeoutReference);
    this.removeHash();
  }

  onModalHide(modal) {
    this.isClosingModal = false;
    this.activeModal = null;
    if (!this.isOpeningModal && this.activeModalTrigger) {
      this.activeModalTrigger.focus();
    }
    if (!modal.backgroundScroll) {
      setTimeout(() => {
        [...document.querySelectorAll('.js-compensate-for-scrollbar')].forEach((el) => {
          el.style.right = '';
        });
      });
    }
  }

  onModalBeforeShow(modal) {
    if (!modal.backgroundScroll) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      setTimeout(() => {
        [...document.querySelectorAll('.js-compensate-for-scrollbar')].forEach((el) => {
          const right = parseInt(
            window.getComputedStyle(el).getPropertyValue('right'),
            10
          );
          el.style.right = `${scrollBarWidth + right}px`;
        });

        const modalScrollBarWidth = window.innerWidth - modal.clientWidth;
        [...modal.querySelectorAll('.js-compensate-for-scrollbar')].forEach((el) => {
          el.style.right = '';
          const right = parseInt(
            window.getComputedStyle(el).getPropertyValue('right'),
            10
          );
          el.style.right = `${modalScrollBarWidth + right}px`;
        });
      });
    }
    if (modal.showHash) {
      this.setHash(`#${modal.id}`);
    } else {
      this.removeHash();
    }
    this.activeModal = modal;
  }

  onModalShow() {
    this.isOpeningModal = false;
  }

  bindModalTriggers() {
    document.addEventListener('click', this.handleModalTriggerClick);
  }

  handleModalTriggerClick = (event) => {
    const modalTrigger = event.target.closest('.js-modal-trigger');
    if (!modalTrigger) return;

    event.preventDefault();

    const modalId = modalTrigger.getAttribute('data-modal-id');
    const modal = this.idModalMap.get(modalId);

    if (!modal) return;

    if (modalTrigger.hasAttribute('data-modal-toggle')) {
      this.handleToggleModal(modalTrigger, modal);
    } else {
      this.openModal(modalTrigger, modal);
    }

    modal.addEventListener('show', () => {
      modalTrigger.setAttribute('data-modal-open', 'true');
    }, { once: true });

    modal.addEventListener('hide', () => {
      modalTrigger.removeAttribute('data-modal-open');
    }, { once: true });
  };

  openModal(modalTrigger, modal) {
    this.isOpeningModal = true;

    if (this.activeModal && !this.isClosingModal) {
      this.activeModal.addEventListener('hide', () => {
        modal.show(modalTrigger);
      }, { once: true });
      this.activeModal.hide();
    } else {
      this.activeModalTrigger = modalTrigger;
      modal.show(modalTrigger);
    }
  }

  handleToggleModal(modalTrigger, modal) {
    const modalOpen = modalTrigger.hasAttribute('data-modal-open');
    const windowHash = window.location.hash;
    const modalIsOpen = modalOpen || this.activeModal === modal;

    if (modalIsOpen) {
      const shouldHideActiveModal =
        this.activeModal &&
        (windowHash === '' || this.activeModal.querySelector(windowHash) == null);
      if (shouldHideActiveModal) {
        this.activeModal.hide();
      }
      modalTrigger.removeAttribute('data-modal-open');
    } else {
      this.openModal(modalTrigger, modal);
      modalTrigger.setAttribute('data-modal-open', 'true');
    }
  }

  bindWindowPopState() {
    window.addEventListener('popstate', this.handlePopState);
  }

  handlePopState = () => {
    const windowHash = window.location.hash;
    if (
      this.activeModal &&
      (windowHash === '' || this.activeModal.querySelector(windowHash) == null)
    ) {
      this.activeModal.hide();
    }
    if (windowHash.length > 1) {
      const modal = document.querySelector(windowHash);
      if (modal?.matches('esign-modal')) {
        const modalInstance = this.idModalMap.get(modal.id);
        if (modalInstance) {
          modalInstance.show();
        }
      }
    }
  };

  setHash(hash) {
    if (window.history.pushState) {
      window.history.pushState(window.history.state, '', hash);
    } else {
      window.location.hash = hash;
    }
  }

  removeHash() {
    const currentHash = window.location.hash.substring(1);
    if (currentHash) {
      const { history, location } = window;
      if (history.pushState) {
        history.replaceState(history.state, '', ' ');
      } else {
        const scrollV = document.body.scrollTop;
        const scrollH = document.body.scrollLeft;
        location.hash = '';
        document.body.scrollTop = scrollV;
        document.body.scrollLeft = scrollH;
      }
    }
  }
}

// Register the custom element
customElements.define('esign-modal', EsignModal);

// Make classes globally available
window.modalManager = new ModalManager();
